// What the workspace home's sheet offers to bring onto a workspace, besides
// its search: the sessions a standard was met in (Look now), where Triage
// left every finding (Triage), and the machines (Machines). Each is listed at
// one size to read, never zoomed: a session, a finding or a machine is dragged
// from here onto any workspace, or clicked onto the one being worked in. A
// finding brings its session and stays open on its card; a machine comes as
// itself, a card of its sessions. With no workspace being worked in
// (`collect`), a click puts a session into the search one starts from, and a
// machine narrows that search.
import { useState } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import { lastingText, matches, whenText } from "./WorkspaceAddOverlay.jsx";
import { SeverityChip } from "./lenses/triageParts.jsx";
import { VERDICT_WORD, agentName, machineLabel, titleOf } from "./fleetModel.js";
import { setDragItem } from "../lib/dragTypes.js";
import { SEVERITY_COLOR, behaviourTone } from "../lib/traceReview.js";

const DOT = " \u00b7 ";
const NUM = new Intl.NumberFormat();
const num = (n) => NUM.format(n);
const plural = (n, one, many = `${one}s`) => `${num(n)} ${n === 1 ? one : many}`;
const SEV_NAMES = ["critical", "high", "medium", "low"];
// A session's findings shown in its Look now block; a section's sessions
// before its Show all.
const FINDINGS_SHOWN = 8;
const SECTION_SHOWN = 6;
const sevColor = (sev) => (SEV_NAMES.includes(sev) ? SEVERITY_COLOR[behaviourTone(sev)] : "var(--muted-soft)");
const findingTitle = (f) => f.title || String(f.category || "").replace(/[-_]+/g, " ") || "A finding";

export const sessionItem = (s) => ({ kind: "session", id: s.id, title: titleOf(s), sessions: [{ id: s.id, title: titleOf(s) }] });
const findingItem = (f, s) => ({ kind: "finding", id: f.id, session: s.id, title: findingTitle(f), sessionTitle: titleOf(s), sessions: [{ id: s.id, title: titleOf(s) }] });
// A machine, as itself: on a workspace, a card of its sessions.
const machineItem = (m) => ({ kind: "machine", id: m.id, name: machineLabel(m.id).name, sessions: [] });
const sessionText = (s) => `${titleOf(s)} ${s.findings.map((f) => findingTitle(f)).join(" ")} ${machineLabel(s.endpointId).name}`;

function Quiet({ children }) {
  return <p className="m-0 px-[16px] py-[16px] text-[12px] leading-[1.45] text-[color:var(--muted)]">{children}</p>;
}

function Eyebrow({ children, tone = null }) {
  return <span className="text-[10px] font-medium uppercase tracking-[0.08em]" style={{ color: tone || "var(--eyebrow)" }}>{children}</span>;
}

// Where a session ran, who ran it, with what, and when.
const whereText = (s) => {
  const { name, person } = machineLabel(s.endpointId);
  return [name, person, agentName(s.agent)].filter(Boolean).join(DOT);
};
const whenLine = (s) => [
  whenText(s.firstMs),
  Number.isFinite(s.firstMs) && Number.isFinite(s.lastMs) ? `ran ${lastingText(s.lastMs - s.firstMs)}` : null,
  s.requests ? plural(s.requests, "request") : null,
].filter(Boolean).join(DOT);

// One session a standard was met in, as the reader decides about it: what it
// was, where and when, every finding with its verdict and what it met. Its
// head drags the session, a finding's row that finding.
function LeadSession({ s, on, onItem, onOpen, collect = false }) {
  const shown = s.findings.slice(0, FINDINGS_SHOWN);
  const laid = on.has(s.id);
  const cell = "flex items-center whitespace-nowrap border-l border-[color:var(--line-soft)] px-[10px] py-[6px] text-[color:var(--muted)] hover:text-[color:var(--ink)]";
  return (
    <article className="mx-[12px] mb-[10px] overflow-hidden rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--bg)]" data-look-now-session={s.id}>
      <div draggable onDragStart={(e) => setDragItem(e, sessionItem(s))} className="cursor-grab px-[12px] pb-[8px] pt-[10px] active:cursor-grabbing" data-look-now-head="">
        <OverflowTooltip text={titleOf(s)} lines={2} className="text-[13.5px] font-medium leading-[18px] text-[color:var(--ink)]" />
        <div className="mt-[4px] truncate text-[11px] leading-[14px] text-[color:var(--muted)]">{whereText(s)}</div>
        <div className="truncate text-[11px] leading-[14px] text-[color:var(--muted)]">{whenLine(s)}</div>
      </div>
      <div className="border-t border-[color:var(--line-soft)] py-[4px]">
        {shown.map((f) => (
          <div
            key={f.id}
            draggable
            onDragStart={(e) => { e.stopPropagation(); setDragItem(e, findingItem(f, s)); }}
            className="grid cursor-grab grid-cols-[8px_minmax(0,1fr)_auto] items-baseline gap-x-[8px] px-[12px] py-[3px] hover:bg-[color:var(--gray-fill)] active:cursor-grabbing"
            data-look-now-finding={f.id}
          >
            <span className="h-[8px] w-[8px] rounded-[1px]" style={{ background: sevColor(f.severity) }} aria-hidden="true" />
            <OverflowTooltip text={findingTitle(f)} className="min-w-0 text-[12px] leading-[16px] text-[color:var(--ink)]" />
            <span className="text-[11px] leading-[16px] text-[color:var(--muted)]">{VERDICT_WORD[f.verdict] || "not verified yet"}</span>
            {f.met?.length ? <span className="col-start-2 col-end-4 text-[11px] leading-[14px] text-[color:var(--danger,#d4503e)]">{`Met ${f.met.join(", ")}`}</span> : null}
          </div>
        ))}
        {s.findings.length > shown.length ? <div className="px-[12px] py-[3px] text-[11px] text-[color:var(--muted-soft)]">{`${plural(s.findings.length - shown.length, "more finding")}`}</div> : null}
      </div>
      <div className="flex items-stretch justify-end border-t border-[color:var(--line-soft)] text-[11px]">
        <button type="button" className={`${cell} ${laid ? "text-[color:var(--ink)]" : ""}`} onClick={() => onItem(sessionItem(s))} aria-pressed={laid}>
          {collect ? (laid ? "In the search \u2713" : "Add to the search") : laid ? "On the workspace \u2713" : "Lay on"}
        </button>
        <HoverTooltip placement="top" label="This session's own workspace, in a pane beside">
          <button type="button" className={cell} onClick={() => onOpen(s)}>Open workspace</button>
        </HoverTooltip>
      </div>
    </article>
  );
}

// Look now: the sessions a standard was met in, the worst first. Nothing
// else leads here: with no standard met in the range it says so.
export function LookNowList({ model, outcomes, needle, on, onItem, onOpen, collect = false }) {
  if (!model.findingsReady) return <Quiet>Reading the findings and what the standards said of them...</Quiet>;
  const met = outcomes.find((o) => o.id === "met");
  if (!met?.groups.length) return <Quiet>Nothing met a standard in this range. Triage lists where every finding was left.</Quiet>;
  const standards = [...new Set(met.findings.flatMap((f) => f.met || []))];
  const groups = met.groups.filter((g) => matches(sessionText(model.sessions.get(g.s.id) || g.s), needle));
  const all = { kind: "session", title: "A standard was met", sessions: met.groups.map((g) => ({ id: g.s.id, title: titleOf(model.sessions.get(g.s.id) || g.s) })) };
  return (
    <div data-look-now="">
      <div className="flex items-start gap-[10px] px-[16px] pb-[10px] pt-[12px]">
        <div className="grid min-w-0 flex-1 gap-[2px]">
          <Eyebrow tone="var(--danger, #d4503e)">A standard was met: a person should look</Eyebrow>
          <span className="text-[11px] leading-[14px] text-[color:var(--muted)]">
            {[`${plural(met.findings.length, "finding")} in ${plural(met.groups.length, "session")}`, standards.length ? `met ${standards.join(", ")}` : null].filter(Boolean).join(DOT)}
          </span>
        </div>
        {met.groups.length > 1 ? (
          <HoverTooltip placement="bottom" label={collect ? "Every session here into the search, to start one workspace with" : "Every session here on one workspace, on one clock"}>
            <button type="button" onClick={() => onItem(all)} className="flex-none rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[3px] text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-look-now-all="">
              {collect ? "Add them all to the search" : "Lay them all on"}
            </button>
          </HoverTooltip>
        ) : null}
      </div>
      {groups.length ? groups.map((g) => <LeadSession key={g.s.id} s={model.sessions.get(g.s.id) || g.s} on={on} onItem={onItem} onOpen={onOpen} collect={collect} />)
        : <Quiet>None of them matches the search.</Quiet>}
    </div>
  );
}

// A session as a row to choose: how bad the worst found is, what it was, its
// findings and where Triage left them, where and when it ran.
function SessionRow({ s, findings = s.findings, on, onItem }) {
  const laid = on.has(s.id);
  const verdicts = new Map();
  for (const f of findings) verdicts.set(VERDICT_WORD[f.verdict] || "not verified yet", (verdicts.get(VERDICT_WORD[f.verdict] || "not verified yet") || 0) + 1);
  const detail = [
    `${plural(findings.length, "finding")}${findings[0] ? `: ${findingTitle(findings[0])}` : ""}`,
    [...verdicts].map(([v, n]) => `${n} ${v}`).join(", "),
  ].filter(Boolean).join(DOT);
  return (
    <button
      type="button"
      draggable
      onDragStart={(e) => setDragItem(e, sessionItem(s))}
      onClick={() => onItem(sessionItem(s))}
      aria-pressed={laid}
      className="relative grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-x-[16px] px-[16px] py-[7px] text-left hover:bg-[color:var(--gray-fill)]"
      data-fleet-source-row={s.id}
    >
      <span className={`absolute left-0 top-[7px] w-[16px] text-center text-[12px] leading-[16px] ${laid ? "text-[color:var(--green)]" : "text-[color:var(--muted-soft)]"}`} aria-hidden="true">{laid ? "\u2713" : "+"}</span>
      <span className="min-w-0">
        <span className="flex min-w-0 items-baseline gap-[6px]">
          <SeverityChip severity={SEV_NAMES[s.worst] || null} />
          <span className="min-w-0 truncate text-[12.5px] leading-[16px] text-[color:var(--ink)]">{titleOf(s)}</span>
        </span>
        <span className="mt-[2px] block truncate text-[11px] leading-[14px] text-[color:var(--muted)]">{detail}</span>
      </span>
      <span className="flex max-w-[170px] flex-col items-end text-[11px] leading-[16px] text-[color:var(--muted)]">
        <span className="max-w-full truncate">{machineLabel(s.endpointId).name}</span>
        <span className="tabular-nums text-[color:var(--muted-soft)]">{whenText(s.firstMs)}</span>
      </span>
    </button>
  );
}

function ShowAll({ n, open, onToggle }) {
  return (
    <button type="button" onClick={onToggle} className="w-full px-[16px] py-[6px] text-left text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">
      {open ? "Show the first few" : `Show all ${num(n)}`}
    </button>
  );
}

// Triage: every outcome in the order the Triage tab keeps, a standard met
// first, each with its sessions, the worst first.
export function TriageList({ model, outcomes, needle, on, onItem }) {
  const [open, setOpen] = useState(() => new Set());
  if (!model.findingsReady) return <Quiet>Reading where Triage left each finding...</Quiet>;
  if (!outcomes.length) return <Quiet>The review kept no findings in this range.</Quiet>;
  return (
    <div data-fleet-triage="">
      {outcomes.map((o) => {
        const groups = o.groups.filter((g) => matches(sessionText(model.sessions.get(g.s.id) || g.s), needle));
        if (!groups.length) return null;
        const all = open.has(o.id);
        const shown = all ? groups : groups.slice(0, SECTION_SHOWN);
        return (
          <section key={o.id} className="pb-[4px]" data-fleet-outcome={o.id}>
            <div className="flex min-w-0 items-baseline gap-[8px] px-[16px] pb-[4px] pt-[12px]">
              <Eyebrow tone={o.id === "met" ? "var(--danger, #d4503e)" : null}>{o.label}</Eyebrow>
              <span className="flex-none text-[11px] tabular-nums text-[color:var(--muted-soft)]">{plural(groups.length, "session")}</span>
              <span className="min-w-0 truncate text-[11px] text-[color:var(--muted)]">{o.why}</span>
            </div>
            {shown.map((g) => <SessionRow key={g.s.id} s={model.sessions.get(g.s.id) || g.s} findings={g.findings} on={on} onItem={onItem} />)}
            {groups.length > SECTION_SHOWN ? <ShowAll n={groups.length} open={all} onToggle={() => setOpen((cur) => { const next = new Set(cur); if (next.has(o.id)) next.delete(o.id); else next.add(o.id); return next; })} /> : null}
          </section>
        );
      })}
    </div>
  );
}

// A machine's range day by day: a bar for each day it ran sessions, as tall
// as how many began, in the colour of the worst found that day.
function DayStrip({ m }) {
  const max = Math.max(1, ...m.days);
  const w = 3;
  return (
    <svg viewBox={`0 0 ${m.days.length * w} 14`} preserveAspectRatio="none" className="block h-[14px] w-[96px]" aria-hidden="true">
      {m.days.map((n, i) => {
        if (!n) return null;
        const h = Math.max(2, (n / max) * 14);
        const worst = m.dayWorst[i];
        const fill = worst < 0 ? "var(--danger, #d4503e)" : worst <= 1 ? "color-mix(in srgb, var(--danger, #d4503e) 60%, transparent)" : worst === 2 ? "var(--warn, #c08a2d)" : "var(--muted-soft)";
        return <rect key={i} x={i * w + 0.5} y={14 - h} width={w - 1} height={h} fill={fill} />;
      })}
    </svg>
  );
}

// Machines: each machine, what most needs a look first; opened, the sessions
// on it the review flagged. Dragged, a machine brings those sessions.
export function MachinesList({ model, needle, on, onItem, collect = false }) {
  const [open, setOpen] = useState(() => new Set());
  if (!model.settled) return <Quiet>Reading the range&apos;s sessions...</Quiet>;
  const list = model.machines.filter((m) => matches(`${machineLabel(m.id).name} ${machineLabel(m.id).person} ${m.flagged.map((s) => titleOf(s)).join(" ")}`, needle));
  if (!list.length) return <Quiet>{needle.length ? "No machine matches the search." : "No sessions in this range."}</Quiet>;
  return (
    <div data-fleet-machines="">
      {list.map((m) => {
        const { name, person } = machineLabel(m.id);
        const isOpen = open.has(m.id);
        const toggle = () => setOpen((cur) => { const next = new Set(cur); if (next.has(m.id)) next.delete(m.id); else next.add(m.id); return next; });
        return (
          <section key={m.id} className={isOpen ? "pb-[6px]" : ""} data-fleet-machine={m.id}>
            <button
              type="button"
              draggable
              onDragStart={(e) => setDragItem(e, machineItem(m))}
              onClick={toggle}
              aria-expanded={isOpen}
              className={`grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-[16px] px-[16px] py-[8px] text-left hover:bg-[color:var(--gray-fill)] ${isOpen ? "bg-[color:var(--gray-fill)]" : ""}`}
            >
              <span className="min-w-0">
                <span className="flex min-w-0 items-baseline gap-[8px]">
                  <span className="min-w-0 truncate text-[12.5px] font-medium leading-[16px] text-[color:var(--ink)]">{name}</span>
                  {m.met ? <span className="flex-none text-[11px] text-[color:var(--danger,#d4503e)]">{`${num(m.met)} met a standard`}</span> : null}
                </span>
                <span className="mt-[2px] block truncate text-[11px] leading-[14px] text-[color:var(--muted)]">
                  {[person, plural(m.sessions.length, "session"), m.flagged.length ? `${num(m.flagged.length)} flagged` : model.findingsReady ? "nothing flagged" : null].filter(Boolean).join(DOT)}
                </span>
              </span>
              <DayStrip m={m} />
            </button>
            {isOpen ? (
              <>
                {(m.flagged.length ? m.flagged : m.clean.slice(0, SECTION_SHOWN)).map((s) => <SessionRow key={s.id} s={s} on={on} onItem={onItem} />)}
                <button type="button" onClick={() => onItem(machineItem(m))} className="w-full px-[16px] py-[6px] text-left text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-fleet-machine-bring="">
                  {collect ? "Narrow the search to the machine" : "Bring the machine onto the workspace"}
                </button>
              </>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

// The sheet's sources from the fleet, for WorkspaceAddPanel's `extraSources`.
export function fleetSources({ model, outcomes, needle, on, onItem, onOpen, collect = false }) {
  const met = outcomes.find((o) => o.id === "met");
  const flagged = new Set(outcomes.flatMap((o) => o.groups.map((g) => g.s.id))).size;
  return [
    { id: "look", label: "Look now", count: model.findingsReady ? num(met?.groups.length || 0) : null, body: <LookNowList model={model} outcomes={outcomes} needle={needle} on={on} onItem={onItem} onOpen={onOpen} collect={collect} /> },
    { id: "triage", label: "Triage", count: model.findingsReady ? num(flagged) : null, body: <TriageList model={model} outcomes={outcomes} needle={needle} on={on} onItem={onItem} /> },
    { id: "machines", label: "Machines", count: model.settled ? num(model.machines.length) : null, body: <MachinesList model={model} needle={needle} on={on} onItem={onItem} collect={collect} /> },
  ];
}
